import { zodResolver } from '@hookform/resolvers/zod'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ArrowLeft, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router'
import { z } from 'zod'

import { getHighestRole, isPrivilegedRole } from '@ankor-io/common/domain/iam/account'
import { toCents } from '@ankor-io/common/domain/pay/currency'
import { Role } from '@ankor-io/common/types/account'
import type { Category } from '@ankor-io/common/types/transaction/category'
import { type CreateTransactionBody, PaymentMethod, VatCode } from '@ankor-io/common/types/transaction/transaction'
import { currencies } from '@ankor-io/common/util/currencies'
import { getPaymentMethodText } from '@ankor-io/common/util/enum-to-string/payment.method'
import { getVatCodeText } from '@ankor-io/common/util/enum-to-string/vat'

import { useCharter } from '@/hooks/useCharter'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Chip } from '@/ui-components/Chip'
import { ErrorMessage, Field, Label } from '@/ui-components/Fieldset'
import { Heading } from '@/ui-components/Heading'
import { Input } from '@/ui-components/Input'
import { Listbox, ListboxOption } from '@/ui-components/Listbox'
import Loading from '@/ui-components/Loading'
import { Select } from '@/ui-components/Select'
import { Textarea } from '@/ui-components/Textarea'
import AddCategoryModal from '@/views/components/AddCategoryModal'

const formSchema = z.object({
  vatCode: z.enum([VatCode.VAT_INCLUSIVE, VatCode.VAT_EXCLUSIVE, VatCode.VAT_NA]),
  // Accept empty string as undefined and coerce non-empty to number
  vat: z.preprocess(
    (val) => (val === '' || val === null || typeof val === 'undefined' ? undefined : Number(val)),
    z.number().min(1, 'Tax amount must be greater than 0').max(100, 'Tax amount must be less than 100').optional(),
  ),
  categoryId: z.string().optional(),
  vendor: z.string().optional(),
  comments: z.string().optional(),
  paymentMethod: z
    .union([z.literal(''), z.nativeEnum(PaymentMethod)])
    .refine((val) => val !== '', { message: 'Please select a payment method' }),
  originalAmount: z
    .string()
    .min(1, 'Amount is required')
    .refine((val) => {
      const num = parseFloat(val)
      return !isNaN(num) && num >= 0 && num <= 9999999.99
    }, 'Amount must be between 0 and 9,999,999.99'),
  originalCurrencyCode: z.string().min(1, 'Currency is required'),
  date: z.string().min(1, 'Date is required'),
})

type FormData = z.infer<typeof formSchema>

const TransactionNewPage: React.FC = () => {
  const { charterId } = useParams()
  const navigate = useNavigate()
  const { getAccessToken } = useKindeAuth()
  const { charter, setCharter } = useCharter()
  const { activeOrganizationId, activeConnectedAccountId, organizations } = useUserContext()

  const [isLoading, setIsLoading] = useState(false)
  const [isLoggingExpense, setIsLoggingExpense] = useState(false)

  const [categories, setCategories] = useState<Category[]>([])
  const [areCategoriesLoading, setAreCategoriesLoading] = useState(true)
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      categoryId: '',
      originalCurrencyCode: 'USD',
      vatCode: VatCode.VAT_INCLUSIVE,
      date: '',
    },
  })

  useEffect(() => {
    const getCharter = async () => {
      setIsLoading(true)
      try {
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charterId}`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!res.ok) {
          throw new Error('Failed to fetch charter')
        }

        const data = await res.json()
        setCharter(data)
      } catch (error) {
        console.error('Error fetching charter:', error)
        toast.error('Failed to fetch charter details')
      } finally {
        setIsLoading(false)
      }
    }

    const getCategories = async () => {
      setAreCategoriesLoading(true)
      try {
        const res = await fetch(`/api/pay/org/${activeOrganizationId}/categories`, {
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        })

        if (!res.ok) {
          throw new Error('Failed to fetch categories')
        }

        const data = await res.json()
        setCategories(data)
      } catch (error) {
        console.error('Error fetching categories:', error)
        toast.error('Failed to fetch categories')
      } finally {
        setAreCategoriesLoading(false)
      }
    }

    if (charterId && charterId !== charter?.id) {
      getCharter()
    }

    getCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charterId, activeOrganizationId, activeConnectedAccountId])

  const handleNewCategory = (category: Category) => {
    setCategories([...categories, category])
  }

  const onSubmit = (data: FormData) => {
    const createTransaction = async () => {
      const payload: CreateTransactionBody = {
        originalAmountCents: toCents(Number(data.originalAmount)),
        originalAmountCurrencyCode: data.originalCurrencyCode,
        paymentMethod: data.paymentMethod as PaymentMethod,
        vatCode: data.vatCode,
        date: new Date(data.date).toISOString(),
        ...(data.vatCode !== VatCode.VAT_NA && data.vatCode !== VatCode.VAT_INCLUSIVE && { vat: data.vat?.toString() }),
        ...(data.categoryId && { categoryId: data.categoryId }),
        ...(data.vendor && { vendor: data.vendor }),
        ...(data.comments && { comments: data.comments }),
      }

      try {
        setIsLoggingExpense(true)
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charterId}/transaction`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to create transaction')
        }

        setCharter(null)
        navigate(`/charters/${charterId}`)
      } catch (error) {
        console.error(error)
        throw new Error('Failed to create transaction')
      } finally {
        setIsLoggingExpense(false)
      }
    }

    toast.promise(createTransaction, {
      loading: 'Creating expense...',
      success: 'Expense created successfully',
      error: 'Failed to create expense',
    })
  }

  if (isLoading) {
    return <Loading />
  }

  if (!charter) {
    return <Heading level={3}>Charter not found</Heading>
  }

  return (
    <>
      <Button
        plain
        className="mb-2"
        to={`/charters/${charterId}`}
        aria-label="Go back"
      >
        <ArrowLeft data-slot="icon" />
        Back
      </Button>

      <Heading
        className="mb-4"
        level={3}
      >
        Log Expense for {charter?.name}
      </Heading>

      <form
        role="form"
        className="space-y-6"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {/* Expense Details */}
        <div className="space-y-2">
          <Heading level={4}>Expense Details</Heading>
          <div className="flex flex-col md:grid md:grid-cols-2 gap-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
            {/* Date */}
            <Field className="space-y-1">
              <Label>Date</Label>
              <Input
                type="date"
                {...form.register('date')}
              />
              {form.formState.errors.date && <ErrorMessage>{form.formState.errors.date.message}</ErrorMessage>}
            </Field>

            {/* Vendor/Supplier */}
            <Field className="space-y-1">
              <Label>Vendor/Supplier</Label>
              <Input
                type="text"
                placeholder="Enter vendor/supplier name"
                {...form.register('vendor')}
              />
              {form.formState.errors.vendor && <ErrorMessage>{form.formState.errors.vendor.message}</ErrorMessage>}
            </Field>

            {/* Category */}
            <Field className="space-y-1">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-x-2">
                <Label>Category</Label>
                {activeOrganizationId &&
                  isPrivilegedRole(getHighestRole(organizations[activeOrganizationId].roles as [Role, ...Role[]])) && (
                    <Button
                      plain
                      className="w-full sm:w-auto"
                      onClick={() => setIsAddCategoryModalOpen(true)}
                    >
                      <Plus data-slot="icon" />
                      New Category
                    </Button>
                  )}
              </div>
              {areCategoriesLoading ? (
                <Loading />
              ) : (
                categories.length > 0 && (
                  <Listbox
                    className={
                      !isPrivilegedRole(getHighestRole(organizations[activeOrganizationId!].roles as [Role, ...Role[]]))
                        ? 'mt-4'
                        : ''
                    }
                    multiple={false}
                    value={form.watch('categoryId')}
                    onChange={(value: string | undefined) =>
                      form.setValue('categoryId', value, { shouldValidate: true })
                    }
                  >
                    <ListboxOption value="">Select a category</ListboxOption>
                    {categories.map((category) => (
                      <ListboxOption
                        key={category.id}
                        value={category.id}
                      >
                        <Chip
                          name={category.name}
                          colour={category.color}
                          icon={category.icon}
                        />
                      </ListboxOption>
                    ))}
                  </Listbox>
                )
              )}
              {form.formState.errors.categoryId && (
                <ErrorMessage>{form.formState.errors.categoryId.message}</ErrorMessage>
              )}
            </Field>

            {/* Payment Method */}
            <Field className="space-y-1 md:mt-[0.1875rem]">
              <Label>Payment Method</Label>
              <Select {...form.register('paymentMethod')}>
                <option value="">Select a payment method</option>
                {Object.values(PaymentMethod).map(
                  (paymentMethod, index) =>
                    paymentMethod !== PaymentMethod.ANKOR_PAY && (
                      <option
                        key={`${paymentMethod}-${index}`}
                        value={paymentMethod}
                      >
                        {getPaymentMethodText(paymentMethod)}
                      </option>
                    ),
                )}
              </Select>
              {form.formState.errors.paymentMethod && (
                <ErrorMessage>{form.formState.errors.paymentMethod.message}</ErrorMessage>
              )}
            </Field>

            {/* Comments */}
            <Field className="col-span-2">
              <Label>Comments</Label>
              <Textarea
                placeholder="Add any relevant details..."
                {...form.register('comments')}
              />
              {form.formState.errors.comments && <ErrorMessage>{form.formState.errors.comments.message}</ErrorMessage>}
            </Field>
          </div>
        </div>

        {/* Financial Details */}
        <div className="space-y-2">
          <Heading level={4}>Financial Details</Heading>
          <div className="space-y-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
            <Field className="space-y-1">
              <Label>Amount</Label>
              <div className="flex gap-x-2 mt-3">
                {/* Currency Code */}
                <div className="basis-40 flex flex-col gap-y-1">
                  <Select {...form.register('originalCurrencyCode')}>
                    {currencies.map((currency) => (
                      <option
                        key={currency.code}
                        value={currency.code}
                      >
                        {currency.code}
                      </option>
                    ))}
                  </Select>
                  {form.formState.errors.originalCurrencyCode && (
                    <ErrorMessage>{form.formState.errors.originalCurrencyCode.message}</ErrorMessage>
                  )}
                </div>

                {/* Amount */}
                <div className="w-full flex flex-col gap-y-1">
                  <Input
                    step="0.01"
                    type="number"
                    placeholder="Enter amount"
                    {...form.register('originalAmount')}
                  />
                  {form.formState.errors.originalAmount && (
                    <ErrorMessage>{form.formState.errors.originalAmount.message}</ErrorMessage>
                  )}
                </div>
              </div>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Tax */}
              <Field className="space-y-1">
                <Label>Tax</Label>
                <Select {...form.register('vatCode')}>
                  {Object.values(VatCode).map((vatCode, index) => (
                    <option
                      key={`${vatCode}-${index}`}
                      value={vatCode}
                    >
                      {getVatCodeText(vatCode)}
                    </option>
                  ))}
                </Select>
                {form.formState.errors.vatCode && <ErrorMessage>{form.formState.errors.vatCode.message}</ErrorMessage>}
              </Field>

              {/* Tax Amount */}
              <Field className="space-y-1">
                <Label>Tax Amount (%)</Label>
                <Input
                  className="disabled:opacity-50 disabled:cursor-not-allowed"
                  type="number"
                  placeholder="Enter tax amount"
                  disabled={form.watch('vatCode') === VatCode.VAT_NA || form.watch('vatCode') === VatCode.VAT_INCLUSIVE}
                  {...form.register('vat')}
                />
                {form.formState.errors.vat && <ErrorMessage>{form.formState.errors.vat.message}</ErrorMessage>}
              </Field>
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex justify-end gap-2 pt-4">
          <Button
            className="disabled:opacity-50 disabled:cursor-not-allowed"
            outline
            to={`/charters/${charterId}`}
            disabled={isLoggingExpense}
          >
            Cancel
          </Button>
          <Button
            className="disabled:opacity-50 disabled:cursor-not-allowed"
            color="primary"
            type="submit"
            disabled={isLoggingExpense}
          >
            {isLoggingExpense ? 'Logging Expense...' : 'Log Expense'}
          </Button>
        </div>
      </form>

      <AddCategoryModal
        isOpen={isAddCategoryModalOpen}
        onClose={() => setIsAddCategoryModalOpen(false)}
        onNewCategory={handleNewCategory}
      />
    </>
  )
}

export default TransactionNewPage
